/**
 * J14 idle screen: after N minutes of idle sit (systems/idleSit.ts) the world stops being drawn (the sim keeps
 * stepping at 60 Hz) and a dark card shows cultivation, the auto budget left and the next daily event. A key,
 * touch or click resumes drawing. main.ts: `idleScreen.update(world, dt)` each frame, `!idleScreen.active` gates the draw.
 */
import { DAILY_EVENTS, eventOn, type World } from '@nine/shared';
import { defineSetting } from '../settingsRegistry';
import { settings } from '../settings';

declare module '../settingsRegistry' {
  interface Settings {
    /** Minutes of idle sit before the screen goes dark: index into IDLE_SCREEN_MINUTES (0 = off). */
    idleScreen: number;
  }
}

export const IDLE_SCREEN_MINUTES = [0, 1, 3, 10] as const;

defineSetting({
  key: 'idleScreen',
  default: 2,
  order: 141,
  range: [0, 3],
  menu: { group: 'data', label: 'หน้าจอพักเมื่อนั่งสมาธิเอง (นาที)', choices: ['ปิด', '1 นาที', '3 นาที', '10 นาที'] },
});

interface IdleSitLike {
  enabled: boolean;
  sat: number;
  poke(): void;
}

class IdleScreen {
  active = false;
  private card: HTMLDivElement | null = null;
  private t = 0;

  constructor() {
    if (typeof window === 'undefined') return; // node tests import the settings
    for (const ev of ['keydown', 'pointerdown', 'touchstart', 'wheel'] as const)
      window.addEventListener(ev, () => this.input(), { passive: true, capture: true });
  }

  private sit: IdleSitLike | null = null;
  private world: World | null = null;
  private input(): void {
    this.sit?.poke();
    this.resume();
  }

  private resume(): void {
    if (!this.active) return;
    this.active = false;
    this.card?.remove();
    this.card = null;
  }

  update(world: World, dt: number): void {
    if (this.world !== world) {
      this.world = world;
      this.sit = world.sys.idleSit;
    }
    const s = this.sit!;
    s.enabled = settings.idleSit;
    const mins = IDLE_SCREEN_MINUTES[settings.idleScreen] ?? 0;
    if (!this.active) {
      if (mins > 0 && s.sat >= mins * 60) this.show(world);
      return;
    }
    if (s.sat <= 0) return this.resume(); // stood up by another rule
    if ((this.t += dt) >= 1) {
      this.t = 0;
      this.fill(world);
    }
  }

  private show(world: World): void {
    this.active = true;
    const d = document.createElement('div');
    d.className = 'ls-card idle-card';
    d.style.cssText =
      'position:fixed;inset:0;margin:auto;width:340px;height:fit-content;z-index:9999;color:#e8dfc4;background:#0c0b0a;' +
      'border:2px double #b9974a;padding:24px 28px;font-size:16px;line-height:1.7;text-align:center;right:auto;bottom:auto';
    const root = document.createElement('div');
    root.style.cssText = 'position:fixed;inset:0;background:#000;z-index:9998';
    root.append(d);
    document.body.append(root);
    this.card = root as unknown as HTMLDivElement;
    this.card.dataset.body = '';
    this.fill(world);
  }

  private fill(world: World): void {
    const d = this.card?.firstElementChild as HTMLElement | undefined;
    if (!d) return;
    const pr = world.progress as unknown as { cultivation: number; autoBudget?: { secs: number } };
    const lines = ['นั่งสมาธิ', `แต้มฝึกปรือ ${Math.floor(pr.cultivation)}`];
    if (pr.autoBudget) {
      const m = Math.floor(pr.autoBudget.secs / 60);
      lines.push(`เวลาออโต้เหลือ ${Math.floor(m / 60)} ชม. ${m % 60} นาที`);
    }
    const h = world.hour;
    const on = DAILY_EVENTS.find((e) => eventOn(e, h));
    const next = on ?? [...DAILY_EVENTS].sort((a, b) => ((a.from - h + 24) % 24) - ((b.from - h + 24) % 24))[0];
    if (next) lines.push(on ? `กำลังมี ${next.name}` : `งานถัดไป ${next.name} ${String(next.from).padStart(2, '0')}:00`);
    lines.push('แตะหรือกดปุ่มใดก็ได้เพื่อกลับ');
    d.replaceChildren(...lines.map((t) => Object.assign(document.createElement('p'), { textContent: t, style: 'margin:0' })));
  }
}

export const idleScreen = new IdleScreen();
